/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-2.3-INVOICE-PAYMENT-FLOWS-V1
 *
 * SuperAdmin-only Client Payments status card with a test-only
 * mock-webhook simulator. Live charges and emails are always
 * displayed as Off. Invoice totals are NEVER modified by this card.
 *
 * Safety:
 *   - Returns null for non-SuperAdmin.
 *   - No card fields rendered.
 *   - No "Go live" / "Enable live charges" button.
 *   - No provider key surfaced.
 *   - Simulate-payment Apply gated by exact phrase
 *     `SIMULATE TEST PAYMENT`.
 *   - Backend refuses non-TEST invoices on apply regardless of
 *     this UI.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  History,
  Info,
  Lock,
  Play,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const APPLY_SIMULATE = "SIMULATE TEST PAYMENT";

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

function OffBadge({ on, label }) {
  return (
    <Badge
      className={
        on
          ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
          : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      }
    >
      {on ? `On — ${label}` : "Off"}
    </Badge>
  );
}

export default function ClientPaymentsAdminCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const [invoiceId, setInvoiceId] = useState("");
  const [simStatus, setSimStatus] = useState("paid");
  const [dryResult, setDryResult] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // EVENTSFLO-CLIENT-PAYMENTS-RECENT-EVENTS-PANEL-V1
  // Read-only recent payment events (last 20).
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsStatusFilter, setEventsStatusFilter] = useState("all");

  // EVENTSFLO-CLIENT-PAYMENTS-EVENTS-METRIC-TILE-V1
  // Read-only aggregate metrics over rolling window.
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const loadMetrics = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setMetricsLoading(true);
    try {
      const body = await authedFetch(
        `/api/admin/client-payments/events/metrics?window_hours=24`,
        token,
      );
      setMetrics(body);
    } catch (e) {
      toast.error(e.message || "Metrics fetch failed");
    } finally {
      setMetricsLoading(false);
    }
  }, [isSuperAdmin, token]);

  const loadEvents = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "20" });
      if (eventsStatusFilter && eventsStatusFilter !== "all") {
        params.set("status", eventsStatusFilter);
      }
      const body = await authedFetch(
        `/api/admin/client-payments/events?${params.toString()}`,
        token,
      );
      setEvents(body);
    } catch (e) {
      toast.error(e.message || "Events fetch failed");
    } finally {
      setEventsLoading(false);
    }
  }, [isSuperAdmin, token, eventsStatusFilter]);

  const loadStatus = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setStatusLoading(true);
    try {
      const body = await authedFetch(`/api/admin/client-payments/status`, token);
      setStatus(body);
    } catch (e) {
      toast.error(e.message || "Status failed");
    } finally {
      setStatusLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const runDryRun = async () => {
    if (!invoiceId.trim()) {
      toast.error("invoice_id required");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`/api/admin/client-payments/mock-webhook`, token, {
        method: "POST",
        body: JSON.stringify({
          invoice_id: invoiceId.trim(),
          status: simStatus,
          dry_run: true,
        }),
      });
      setDryResult(body);
      if (body.blockers?.length) {
        toast.warning(`Blocked — ${body.blockers.join(", ")}`);
      } else {
        toast.success("Dry-run OK (no invoice totals changed)");
      }
    } catch (e) {
      toast.error(e.message || "Dry-run failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applySim = async () => {
    if (confirmPhrase !== APPLY_SIMULATE) {
      toast.error("Confirmation phrase does not match.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`/api/admin/client-payments/mock-webhook`, token, {
        method: "POST",
        body: JSON.stringify({
          invoice_id: invoiceId.trim(),
          status: simStatus,
          dry_run: false,
          confirm: APPLY_SIMULATE,
        }),
      });
      if (body.blockers?.length) {
        toast.warning(`Blocked — ${body.blockers.join(", ")}`);
      } else {
        toast.success(`Simulated ${body.updated_status} (no charge, no email)`);
      }
      setConfirmPhrase("");
    } catch (e) {
      toast.error(e.message || "Apply failed");
    } finally {
      setActionLoading(false);
    }
  };

  const applyEnabled = useMemo(
    () =>
      confirmPhrase === APPLY_SIMULATE &&
      !actionLoading &&
      dryResult &&
      !dryResult.blockers?.length,
    [confirmPhrase, actionLoading, dryResult],
  );

  if (!isSuperAdmin) return null;

  return (
    <Card data-testid="client-payments-admin-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Client invoice payments (admin)
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. Mock provider only.{" "}
          <strong className="text-zinc-300">No real charge</strong> is
          made.{" "}
          <strong className="text-zinc-300">No card fields</strong>{" "}
          are collected.{" "}
          <strong className="text-zinc-300">Invoice totals are
          never modified</strong> by this card.
        </p>
      </CardHeader>
      <CardContent>
        {/* Banner */}
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="client-payments-safety-banner"
        >
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5" />
          <span>
            production_charge: <strong>false</strong>. email_sent:
            <strong> false</strong>. Live charges and payment
            emails are disabled.
          </span>
        </div>

        {/* Status */}
        <div className="mt-3 rounded-md border border-zinc-700/40 p-3" data-testid="client-payments-status-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Status
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadStatus}
              disabled={statusLoading}
              data-testid="client-payments-status-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          {!status ? (
            <div className="text-xs text-zinc-500">Loading status…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-zinc-400 text-xs">payments enabled</div>
                <div className="mt-0.5" data-testid="client-payments-flag-enabled">
                  <OffBadge on={status.payments_enabled} label="enabled" />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">provider</div>
                <div className="text-zinc-300 mt-0.5 font-mono text-xs" data-testid="client-payments-provider">
                  {status.provider}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">live charges</div>
                <div className="mt-0.5" data-testid="client-payments-flag-live-charges">
                  <OffBadge on={status.live_charges_enabled} label="live" />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">payment emails</div>
                <div className="mt-0.5" data-testid="client-payments-flag-emails">
                  <OffBadge on={status.emails_enabled} label="emails" />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">configured</div>
                <div className="text-zinc-300 mt-0.5">{String(status.configured)}</div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">currencies</div>
                <div className="text-zinc-300 mt-0.5 font-mono text-xs">
                  {(status.supported_currencies || []).join(", ")}
                </div>
              </div>
              <div className="col-span-2 md:col-span-4 text-xs text-zinc-500">
                {status.note}
              </div>
            </div>
          )}
        </div>

        {/* Mock webhook simulator */}
        <div className="mt-3 rounded-md border border-zinc-700/40 p-3" data-testid="client-payments-simulator-section">
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Play className="w-3.5 h-3.5" /> Simulate TEST payment webhook
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
            <div>
              <Label htmlFor="client-payments-invoice-id-input" className="text-xs text-zinc-400">
                invoice_id (must be a TEST invoice for Apply)
              </Label>
              <Input
                id="client-payments-invoice-id-input"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                placeholder="TEST invoice id"
                className="font-mono text-xs"
                data-testid="client-payments-invoice-id-input"
              />
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-1">simulated status</div>
              <Select value={simStatus} onValueChange={setSimStatus}>
                <SelectTrigger data-testid="client-payments-sim-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">paid</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="cancelled">cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={runDryRun}
                disabled={actionLoading || !invoiceId.trim()}
                data-testid="client-payments-sim-dry-run-btn"
              >
                <Play className="w-3.5 h-3.5 mr-1" /> Dry-run
              </Button>
            </div>
          </div>
          {dryResult ? (
            <div
              className="rounded border border-zinc-700/40 bg-zinc-900/40 p-2 text-xs grid grid-cols-2 md:grid-cols-4 gap-2 mb-2"
              data-testid="client-payments-sim-dry-run-result"
            >
              <div>
                <div className="text-zinc-400">dry_run</div>
                <div className="text-zinc-300">{String(dryResult.dry_run)}</div>
              </div>
              <div>
                <div className="text-zinc-400">production_charge</div>
                <div className="text-zinc-300">{String(dryResult.production_charge)}</div>
              </div>
              <div>
                <div className="text-zinc-400">email_sent</div>
                <div className="text-zinc-300">{String(dryResult.email_sent)}</div>
              </div>
              <div>
                <div className="text-zinc-400">blockers</div>
                <div className="text-zinc-300">{(dryResult.blockers || []).length}</div>
              </div>
              {dryResult.blockers?.length ? (
                <div className="col-span-2 md:col-span-4 text-rose-300">
                  {dryResult.blockers.join(", ")}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-2">
            <Label htmlFor="client-payments-confirm-input" className="text-xs text-zinc-400">
              Type exactly to enable Apply:{" "}
              <span className="font-mono text-amber-300">{APPLY_SIMULATE}</span>
            </Label>
            <div className="flex flex-wrap gap-2 items-center mt-1">
              <Input
                id="client-payments-confirm-input"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder={APPLY_SIMULATE}
                className="max-w-md font-mono text-xs"
                data-testid="client-payments-confirm-input"
              />
              <Button
                onClick={applySim}
                disabled={!applyEnabled}
                data-testid="client-payments-apply-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Apply simulation
              </Button>
              {!applyEnabled ? (
                <div
                  className="text-xs text-zinc-400 flex items-center gap-1"
                  data-testid="client-payments-apply-helper"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  Dry-run (no blockers), then exact phrase required.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* EVENTSFLO-CLIENT-PAYMENTS-EVENTS-METRIC-TILE-V1
            Read-only aggregate metrics over the last 24h. No
            retry/resend/mark-paid/apply/live-charge controls. */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="client-payments-events-metric-tile"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" />
              Payment events metrics
              {metrics ? (
                <Badge
                  data-testid="client-payments-metrics-warning-level"
                  className={
                    metrics.warning_level === "attention"
                      ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                      : metrics.warning_level === "watch"
                      ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                      : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  }
                >
                  {metrics.warning_level}
                </Badge>
              ) : null}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadMetrics}
              disabled={metricsLoading}
              data-testid="client-payments-metrics-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          <div
            className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-2 text-xs text-zinc-300 flex items-start gap-2 mb-2"
            data-testid="client-payments-metrics-readonly-note"
          >
            <Lock className="w-3.5 h-3.5 mt-0.5" />
            <span>
              Read-only mock payment metrics. No charges, emails
              or invoice changes can be triggered from this tile.
            </span>
          </div>
          {metrics ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-xs"
              data-testid="client-payments-metrics-grid"
            >
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Events last {metrics.window_hours}h</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="client-payments-metrics-total"
                >
                  {metrics.total_events}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Blocked</div>
                <div
                  className="text-rose-300 text-base font-semibold"
                  data-testid="client-payments-metrics-blocked"
                >
                  {metrics.blocked_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Failed</div>
                <div
                  className="text-amber-300 text-base font-semibold"
                  data-testid="client-payments-metrics-failed"
                >
                  {metrics.failed_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Pending</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="client-payments-metrics-pending"
                >
                  {metrics.pending_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Paid</div>
                <div
                  className="text-emerald-300 text-base font-semibold"
                  data-testid="client-payments-metrics-paid"
                >
                  {metrics.paid_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Unique invoices</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="client-payments-metrics-unique-invoices"
                >
                  {metrics.unique_invoice_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Unique clients</div>
                <div
                  className="text-zinc-100 text-base font-semibold"
                  data-testid="client-payments-metrics-unique-clients"
                >
                  {metrics.unique_client_count}
                </div>
              </div>
              <div className="rounded border border-zinc-700/40 p-2">
                <div className="text-zinc-500">Last event</div>
                <div
                  className="text-zinc-100 text-xs"
                  data-testid="client-payments-metrics-last-event"
                >
                  {metrics.last_event_at
                    ? formatWhen(metrics.last_event_at)
                    : "—"}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="text-xs text-zinc-500"
              data-testid="client-payments-metrics-empty"
            >
              {metricsLoading
                ? "Loading payment events metrics…"
                : "No payment events metrics available."}
            </div>
          )}
          {metrics ? (
            <div
              className="text-xs text-zinc-500 mt-2"
              data-testid="client-payments-metrics-footer"
            >
              provider: <strong>{metrics.provider}</strong>
              {" · "}production_charge: <strong>false</strong>
              {" · "}email_sent: <strong>false</strong>
              {" · "}live_charges_enabled: <strong>false</strong>
              {" · "}dry-runs: {metrics.dry_run_count}
              {" · "}applies: {metrics.apply_count}
            </div>
          ) : null}
        </div>

        {/* EVENTSFLO-CLIENT-PAYMENTS-RECENT-EVENTS-PANEL-V1
            Read-only recent payment events (last 20). No retry,
            resend, mark-paid, webhook apply or live-charge controls
            inside this section. */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="client-payments-events-section"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <History className="w-3.5 h-3.5" /> Recent payment events
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-xs text-zinc-400">filter</div>
              <Select
                value={eventsStatusFilter}
                onValueChange={setEventsStatusFilter}
              >
                <SelectTrigger
                  className="h-8 w-[140px]"
                  data-testid="client-payments-events-status-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all statuses</SelectItem>
                  <SelectItem value="pending">pending</SelectItem>
                  <SelectItem value="paid">paid</SelectItem>
                  <SelectItem value="failed">failed</SelectItem>
                  <SelectItem value="cancelled">cancelled</SelectItem>
                  <SelectItem value="blocked">blocked</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={loadEvents}
                disabled={eventsLoading}
                data-testid="client-payments-events-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div
            className="rounded-md border border-zinc-500/30 bg-zinc-500/5 p-2 text-xs text-zinc-300 flex items-start gap-2 mb-2"
            data-testid="client-payments-events-readonly-note"
          >
            <Lock className="w-3.5 h-3.5 mt-0.5" />
            <span>
              Read-only payment event history. No charges, emails
              or invoice changes can be triggered from this panel.
              Mock/test audit records only. No card data is stored
              or shown.
            </span>
          </div>
          {events && Array.isArray(events.rows) && events.rows.length > 0 ? (
            <div className="overflow-auto rounded border border-zinc-700/40 max-h-72">
              <table
                className="w-full text-xs"
                data-testid="client-payments-events-table"
              >
                <thead className="bg-zinc-800/60 text-zinc-400 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">When</th>
                    <th className="text-left px-2 py-1">Invoice</th>
                    <th className="text-left px-2 py-1">Client</th>
                    <th className="text-left px-2 py-1">Provider</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-left px-2 py-1">Amount</th>
                    <th className="text-left px-2 py-1">Production charge?</th>
                    <th className="text-left px-2 py-1">Email sent?</th>
                    <th className="text-left px-2 py-1">Actor</th>
                    <th className="text-left px-2 py-1">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {events.rows.map((row, idx) => (
                    <tr
                      key={row.event_id || `evt-${idx}`}
                      className="odd:bg-zinc-900/30"
                      data-testid={`client-payments-events-row-${idx}`}
                    >
                      <td className="px-2 py-1 text-zinc-300 whitespace-nowrap">
                        {formatWhen(row.created_at)}
                      </td>
                      <td className="px-2 py-1 font-mono text-zinc-300">
                        {row.invoice_number ||
                          (row.invoice_id ? `${row.invoice_id.slice(0, 8)}…` : "—")}
                      </td>
                      <td className="px-2 py-1 font-mono text-zinc-300">
                        {row.client_id ? `${row.client_id.slice(0, 8)}…` : "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-300">{row.provider}</td>
                      <td
                        className="px-2 py-1"
                        data-testid={`client-payments-events-row-${idx}-status`}
                      >
                        <Badge
                          className={
                            row.blocked
                              ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                              : row.status === "paid"
                              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                              : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
                          }
                        >
                          {row.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 text-zinc-300">
                        {row.amount !== null && row.amount !== undefined
                          ? `${Number(row.amount).toFixed(2)} ${row.currency || ""}`.trim()
                          : "—"}
                      </td>
                      <td
                        className="px-2 py-1"
                        data-testid={`client-payments-events-row-${idx}-production-charge`}
                      >
                        <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          {String(row.production_charge)}
                        </Badge>
                      </td>
                      <td
                        className="px-2 py-1"
                        data-testid={`client-payments-events-row-${idx}-email-sent`}
                      >
                        <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          {String(row.email_sent)}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 text-zinc-300">
                        {row.actor_user_id
                          ? `sa:${row.actor_user_id.slice(0, 8)}…`
                          : row.actor_client_user_id
                          ? `client:${row.actor_client_user_id.slice(0, 8)}…`
                          : "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-300">{row.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="text-xs text-zinc-500"
              data-testid="client-payments-events-empty"
            >
              {eventsLoading
                ? "Loading recent payment events…"
                : "No recent payment events recorded."}
            </div>
          )}
          {events ? (
            <div
              className="text-xs text-zinc-500 mt-2"
              data-testid="client-payments-events-summary"
            >
              showing {events.returned_count} of {events.total_available}
              {events.applied_status
                ? ` (filtered status=${events.applied_status})`
                : ""}
              {" · "}production_charge: <strong>false</strong>
              {" · "}email_sent: <strong>false</strong>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
